import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { motion } from 'framer-motion';

import AdminLayout from '../../../components/layout/AdminLayout';
import Table from '../../../components/ui/Table';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import Input from '../../../components/ui/Input';
import Spinner from '../../../components/ui/Spinner';
import Alert from '../../../components/ui/Alert';
import Badge from '../../../components/ui/Badge';
import Card from '../../../components/ui/Card';
import { useFirestoreQuery } from '../../../hooks/useFirestoreQuery';
import { ICliente as Cliente, ITransacao as Transacao } from '../../../types/firestore.types';
import { formatarMoeda } from '../../../utils/currencyUtils';

const ClientManagementPage: React.FC = () => {
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Cliente | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const itemsPerPage = 10;

  // Buscar clientes
  const {
    dados: clientes,
    carregando: carregandoClientes,
    erro: erroClientes,
  } = useFirestoreQuery<Cliente>(
    'clientes',
    [],
    { campo: 'criado_em', direcao: 'desc' }
  );

  // Buscar transações para calcular estatísticas dos clientes
  const {
    dados: transacoes,
    carregando: carregandoTransacoes,
  } = useFirestoreQuery<Transacao>('transacoes');

  // Calcular estatísticas dos clientes
  const clientesComEstatisticas = useMemo(() => {
    if (!clientes || !transacoes) return [];

    return clientes.map(cliente => {
      const transacoesCliente = transacoes.filter(
        transacao => transacao.userId === cliente.id || transacao.cliente_id === cliente.id
      );

      const transacoesCompletas = transacoesCliente.filter(t => 
        t.status === 'completed' || t.status === 'concluido'
      );

      const totalGasto = transacoesCompletas
        .filter(t => t.tipo_operacao === 'debito')
        .reduce((sum, t) => sum + (t.valor || 0), 0);

      const totalRecarga = transacoesCompletas
        .filter(t => t.tipo_operacao === 'credito')
        .reduce((sum, t) => sum + (t.valor || 0), 0);

      const saldoAtual = totalRecarga - totalGasto;
      const isAtivo = transacoesCompletas.length > 0;

      return {
        ...cliente,
        estatisticas: {
          totalTransacoes: transacoesCompletas.length,
          totalGasto,
          totalRecarga,
          saldoAtual,
          isAtivo,
          ultimaTransacao: transacoesCompletas[0]?.criado_em || null
        }
      };
    });
  }, [clientes, transacoes]);

  // Filtros e busca
  const filteredClients = useMemo(() => {
    if (!clientesComEstatisticas) return [];

    let filtered = clientesComEstatisticas;

    // Filtro por status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(client => 
        statusFilter === 'active' ? client.estatisticas.isAtivo : !client.estatisticas.isAtivo
      );
    }

    // Filtro por busca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(client =>
        client.nome.toLowerCase().includes(term) ||
        (client.email && client.email.toLowerCase().includes(term)) ||
        (client.telefone && client.telefone.includes(term))
      );
    }

    return filtered;
  }, [clientesComEstatisticas, searchTerm, statusFilter]);

  // Paginação
  const paginatedClients = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredClients.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredClients, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);

  // Handlers
  const handleViewDetails = (client: any) => {
    setSelectedClient(client);
    setIsDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedClient(null);
  };

  const handleExportData = () => {
    // Implementação básica de exportação
    const csvContent = [
      ['Nome', 'Email', 'Telefone', 'Total Gasto', 'Total Recarga', 'Saldo', 'Status', 'Última Transação'],
      ...filteredClients.map(client => [
        client.nome,
        client.email || 'N/A',
        client.telefone || 'N/A',
        formatarMoeda(client.estatisticas.totalGasto),
        formatarMoeda(client.estatisticas.totalRecarga),
        formatarMoeda(client.estatisticas.saldoAtual),
        client.estatisticas.isAtivo ? 'Ativo' : 'Inativo',
        client.estatisticas.ultimaTransacao 
          ? format(client.estatisticas.ultimaTransacao.toDate(), 'dd/MM/yyyy HH:mm', { locale: ptBR })
          : 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clientes_tugascoins_${format(new Date(), 'dd-MM-yyyy')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Colunas da tabela
  const columns = useMemo(() => [
    {
      titulo: 'Cliente',
      chave: 'cliente',
      render: (cliente: any) => (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0 w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
            <span className="text-primary font-semibold text-sm">
              {cliente.nome.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="font-semibold text-light">{cliente.nome}</div>
            <div className="text-sm text-secondary">{cliente.email || 'Sem email'}</div>
          </div>
        </div>
      ),
    },
    {
      titulo: 'Telefone',
      chave: 'telefone',
      render: (cliente: any) => (
        <span className="text-secondary font-medium">
          {cliente.telefone || 'N/A'}
        </span>
      ),
    },
    {
      titulo: 'Saldo',
      chave: 'saldo',
      render: (cliente: any) => (
        <span className={`font-bold ${
          cliente.estatisticas.saldoAtual >= 0 ? 'text-green-500' : 'text-red-500'
        }`}>
          {formatarMoeda(cliente.estatisticas.saldoAtual)}
        </span>
      ),
    },
    {
      titulo: 'Status',
      chave: 'status',
      render: (cliente: any) => (
        <Badge
          tipo={cliente.estatisticas.isAtivo ? 'success' : 'secondary'}
          className="text-xs"
        >
          {cliente.estatisticas.isAtivo ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      titulo: 'Transações',
      chave: 'transacoes',
      render: (cliente: any) => (
        <span className="text-secondary font-medium">
          {cliente.estatisticas.totalTransacoes}
        </span>
      ),
    },
    {
      titulo: 'Cadastrado',
      chave: 'criado_em',
      render: (cliente: any) => (
        <span className="text-secondary text-sm">
          {cliente.criado_em ? format(cliente.criado_em.toDate(), 'dd/MM/yyyy', { locale: ptBR }) : 'N/A'}
        </span>
      ),
    },
    {
      titulo: 'Ações',
      chave: 'acoes',
      render: (cliente: any) => (
        <div className="flex space-x-2">
          <Button
            onClick={() => handleViewDetails(cliente)}
            tipo="primary"
            className="px-3 py-1 text-sm"
          >
            Detalhes
          </Button>
        </div>
      ),
    },
  ], []);

  const carregando = carregandoClientes || carregandoTransacoes;
  const erro = erroClientes;

  return (
    <AdminLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="container mx-auto p-6 bg-background text-text min-h-screen"
      >
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-primary mb-2">
            Gerenciamento de Clientes
          </h1>
          <p className="text-secondary">
            Gerencie e visualize informações dos clientes do sistema TugasCoins
          </p>
        </div>

        {/* Estatísticas Rápidas */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
        >
          <Card className="p-6 text-center">
            <div className="text-3xl font-bold text-primary mb-2">
              {clientesComEstatisticas.length}
            </div>
            <div className="text-secondary text-sm">Total de Clientes</div>
          </Card>
          <Card className="p-6 text-center">
            <div className="text-3xl font-bold text-green-500 mb-2">
              {clientesComEstatisticas.filter(c => c.estatisticas.isAtivo).length}
            </div>
            <div className="text-secondary text-sm">Clientes Ativos</div>
          </Card>
          <Card className="p-6 text-center">
            <div className="text-3xl font-bold text-blue-500 mb-2">
              {clientesComEstatisticas.reduce((sum, c) => sum + c.estatisticas.totalTransacoes, 0)}
            </div>
            <div className="text-secondary text-sm">Total de Transações</div>
          </Card>
          <Card className="p-6 text-center">
            <div className="text-3xl font-bold text-purple-500 mb-2">
              {formatarMoeda(clientesComEstatisticas.reduce((sum, c) => sum + c.estatisticas.totalGasto, 0))}
            </div>
            <div className="text-secondary text-sm">Volume em Débitos</div>
          </Card>
        </motion.div>

        {/* Filtros e Busca */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mb-6"
        >
          <Card className="p-6">
            <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                <Input
                  tipo="text"
                  placeholder="Buscar por nome, email ou telefone..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="min-w-[300px]"
                />
                
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as any);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 border border-primary/20 rounded-lg bg-background text-text focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">Todos os status</option>
                  <option value="active">Ativos</option>
                  <option value="inactive">Inativos</option>
                </select>
              </div>

              <Button
                onClick={handleExportData}
                tipo="secondary"
                className="whitespace-nowrap"
                disabled={filteredClients.length === 0}
              >
                📊 Exportar Dados
              </Button>
            </div>
          </Card>
        </motion.div>

        {/* Tabela */}
        {carregando && (
          <div className="flex justify-center items-center h-64">
            <Spinner size="lg" color="primary" />
            <span className="ml-4 text-text">Carregando clientes...</span>
          </div>
        )}

        {erro && (
          <Alert tipo="error" mensagem={`Erro ao carregar clientes: ${erro}`} />
        )}

        {!carregando && !erro && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <Card className="overflow-hidden">
              <Table
                colunas={columns}
                dados={paginatedClients}
                className="min-w-full"
              />
              
              {filteredClients.length === 0 && (
                <div className="p-8 text-center text-secondary">
                  <div className="text-6xl mb-4">👥</div>
                  <p className="text-lg font-medium mb-2">Nenhum cliente encontrado</p>
                  <p className="text-secondary">
                    {searchTerm || statusFilter !== 'all' 
                      ? 'Tente ajustar os filtros de busca' 
                      : 'Não há clientes cadastrados no sistema'
                    }
                  </p>
                </div>
              )}

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-t border-primary/20 bg-background-dark">
                  <div className="text-secondary text-sm mb-4 sm:mb-0">
                    Mostrando {paginatedClients.length} de {filteredClients.length} clientes
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      tipo="secondary"
                      className="px-3 py-1 text-sm"
                    >
                      ← Anterior
                    </Button>
                    <span className="mx-4 text-light text-sm">
                      Página {currentPage} de {totalPages}
                    </span>
                    <Button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      tipo="secondary"
                      className="px-3 py-1 text-sm"
                    >
                      Próxima →
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </motion.div>
        )}

        {/* Modal de Detalhes */}
        {selectedClient && (
          <Modal
            isOpen={isDetailsModalOpen}
            onClose={handleCloseDetailsModal}
            titulo={`Detalhes do Cliente`}
            tamanho="lg"
          >
            <div className="p-1 space-y-6">
              {/* Informações Básicas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-10">
                <div>
                  <h4 className="text-lg font-semibold text-primary mb-4">Informações Pessoais</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-secondary text-sm">Nome Completo</label>
                      <p className="text-light font-medium">{selectedClient.nome}</p>
                    </div>
                    <div>
                      <label className="text-secondary text-sm">Telefone</label>
                      <p className="text-light font-medium">{selectedClient.telefone || 'Não informado'}</p>
                    </div>
                    <div>
                      <label className="text-secondary text-sm">Email</label>
                      <p className="text-light font-medium">{selectedClient.email || 'Não informado'}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-primary mb-4">Estatísticas</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-secondary text-sm">Status</label>
                      <div className="mt-1">
                        <Badge tipo={selectedClient.estatisticas.isAtivo ? 'success' : 'secondary'}>
                          {selectedClient.estatisticas.isAtivo ? 'Cliente Ativo' : 'Cliente Inativo'}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <label className="text-secondary text-sm">Total de Transações</label>
                      <p className="text-light font-medium">{selectedClient.estatisticas.totalTransacoes}</p>
                    </div>
                    <div>
                      <label className="text-secondary text-sm">Saldo Atual</label>
                      <p className={`text-lg font-bold ${
                        selectedClient.estatisticas.saldoAtual >= 0 ? 'text-green-500' : 'text-red-500'
                      }`}>
                        {formatarMoeda(selectedClient.estatisticas.saldoAtual)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detalhes Financeiros */}
              <div className="border-t border-primary/20 pt-6">
                <h4 className="text-lg font-semibold text-primary mb-4">Detalhes Financeiros</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Card className="p-1 text-center bg-background">
                    <div className="text-2xl font-bold text-white mb-1">
                      {formatarMoeda(selectedClient.estatisticas.totalRecarga)}
                    </div>
                    <div className="text-secondary text-sm">Total em Recargas</div>
                  </Card>
                  <Card className="p-1 text-center bg-background">
                    <div className="text-2xl font-bold text-white mb-1">
                      {formatarMoeda(selectedClient.estatisticas.totalGasto)}
                    </div>
                    <div className="text-secondary text-sm">Total em Débitos</div>
                  </Card>
                  <Card className="p-1 text-center bg-background">
                    <div className="text-2xl font-bold text-white mb-1">
                      {selectedClient.estatisticas.totalTransacoes}
                    </div>
                    <div className="text-secondary text-sm">Transações Realizadas</div>
                  </Card>
                </div>
              </div>

              {/* Informações do Sistema */}
              <div className="border-t border-primary/20 pt-6">
                <h4 className="text-lg font-semibold text-primary mb-4">Informações do Sistema</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-secondary">ID do Cliente</label>
                    <p className="text-light font-mono">{selectedClient.id}</p>
                  </div>
                  <div>
                    <label className="text-secondary">Data de Cadastro</label>
                    <p className="text-light">
                      {selectedClient.criado_em ? 
                        format(selectedClient.criado_em.toDate(), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR }) 
                        : 'N/A'
                      }
                    </p>
                  </div>
                  <div>
                    <label className="text-secondary">Última Atualização</label>
                    <p className="text-light">
                      {selectedClient.atualizado_em ? 
                        format(selectedClient.atualizado_em.toDate(), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR }) 
                        : 'N/A'
                      }
                    </p>
                  </div>
                  <div>
                    <label className="text-secondary">Última Transação</label>
                    <p className="text-light">
                      {selectedClient.estatisticas.ultimaTransacao ? 
                        format(selectedClient.estatisticas.ultimaTransacao.toDate(), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR }) 
                        : 'Nenhuma transação'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end p-6 border-t border-primary/20 bg-background-dark">
              <Button onClick={handleCloseDetailsModal} tipo="primary">
                Fechar
              </Button>
            </div>
          </Modal>
        )}
      </motion.div>
    </AdminLayout>
  );
};

export default ClientManagementPage;